/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useContext, useState, useMemo } from 'react';
import { contexts, hooks, unstable, utils } from '@broadleaf/admin-components';
import { get, isNil, isString } from 'lodash';

const { ToastContainerContext } = contexts;
const { usePollEffect, useEventCallback } = hooks;
const { DownloadLink, ModalFormAction, SVG, MetadataUtils, RequestUtils } =
  unstable;
const { log, RequestUtils: Request } = utils;
const { getAttribute, getEndpointByType } = MetadataUtils;
const { compile, getErrorMessage } = RequestUtils;
const logger = log.getLogger(
  'form.components.TreeView.components.action-components.global.ExportTranslations'
);
const defaultClassName =
  'tw-flex tw-items-center tw-justify-start tw-w-full tw-py-4 tw-px-3 tw-text-gray-700 tw-border-t tw-border-gray-300 tw-font-medium tw-text-lg tw-whitespace-no-wrap tw-bg-gray-100 hover:tw-bg-gray-200 hover:tw-text-gray-900 focus:tw-bg-gray-200 focus:tw-text-gray-900 focus:tw-outline-none active:tw-bg-gray-300 md:tw-py-2 md:tw-text-sm';

const ExportTranslations = props => {
  const polling = usePollExportById(props);
  const { actionDefinition, readOnly: disabled = false } = props;
  const startExport = useStartExport({ ...props, polling });
  const renderAction = useCallback(
    ({ toggleOpen }) => (
      <button
        className={defaultClassName}
        disabled={disabled}
        onClick={toggleOpen}
      >
        <SVG
          className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
          name="directions"
        />{' '}
        {actionDefinition.label}
      </button>
    ),
    [actionDefinition, disabled]
  );

  return (
    <ModalFormAction
      components={actionDefinition.components}
      disabled={disabled}
      onSubmit={startExport}
      renderAction={renderAction}
      submitLabel={getAttribute(actionDefinition, 'submitLabel', 'Submit')}
      title={actionDefinition.label}
      tooltipPlacement={'top'}
    />
  );
};

/**
 * Starts polling of the created export to check the status.
 * If the export is completed, a message appears with a link to download the file.
 */
function usePollExportById(props) {
  const [active, setActive] = useState(false);
  const [exportId, setExportId] = useState(null);

  const polling = {
    active,
    startPoll: exportId => {
      setExportId(exportId);
      setActive(true);
    },
    stopPoll: () => setActive(false)
  };

  const readResult = useReadExportById({ ...props, polling });

  const options = useMemo(
    () => ({
      strategy: {
        ...usePollEffect.defaultStrategy,
        interval: 2000
      },
      exportId
    }),
    [exportId]
  );

  usePollEffect(readResult, active, options);

  return polling;
}

/**
 * Reads the export by its id. It requires the endpoint with "READ_EXPORT" type.
 */
function useReadExportById(props) {
  const { actionDefinition, getContextParams, polling } = props;
  const toast = useContext(ToastContainerContext);

  const config = useMemo(() => {
    const exportConfig = getEndpointByType(actionDefinition, 'READ_EXPORT');

    const downloadExportUri = getAttribute(
      actionDefinition,
      'downloadExportUri'
    );

    return {
      exportConfig,
      exportContextParams: getContextParams({}),
      downloadExportUri
    };
  }, [actionDefinition, getContextParams]);

  return useEventCallback(
    ({ exportId }) => {
      const context = { ...config.exportContextParams, exportId };

      return Request.request(config.exportConfig, context)
        .then(response => response.data)
        .then(lastExport => {
          const status = get(lastExport, 'status');

          if (
            isNil(lastExport) ||
            status === 'FINISHED' ||
            status === 'ERROR'
          ) {
            polling.stopPoll();
          }

          if (status === 'FINISHED') {
            const downloadExportUri = compile(config.downloadExportUri)(
              context
            );

            toast.addToast(
              <>
                <div>
                  {getAttribute(
                    actionDefinition,
                    'succeededMessage',
                    'The export completed successfully!'
                  )}
                </div>
                <DownloadLink
                  href={downloadExportUri}
                  scope={config.exportConfig.scope}
                  label={getAttribute(
                    actionDefinition,
                    'downloadExportLabel',
                    'Download the export file'
                  )}
                />
              </>,
              {
                type: 'success'
              }
            );
          } else if (status === 'ERROR') {
            const error = get(
              lastExport,
              'errorMessage',
              getAttribute(
                actionDefinition,
                'errorMessage',
                'The export failed'
              )
            );
            toast.addToast(error, { type: 'error' });
          }
        })
        .catch(error => {
          polling.stopPoll();
          if (!isString(error)) {
            error = getErrorMessage(error);
          }

          toast.addToast(error, {
            autoClose: 5000,
            closeOnClick: true,
            type: 'error'
          });

          logger.debug('An error occurred while reading the exports: ', error);
          return Promise.reject(error);
        });
    },
    [config]
  );
}

/**
 * This hook is used to start an export. It requires the endpoint with "START_EXPORT" type.
 * If the export started successfully the polling of this export will be started to check the status.
 * @see usePollExportById
 */
function useStartExport(props) {
  const { actionDefinition, getContextParams, polling } = props;
  const toast = useContext(ToastContainerContext);
  const contextParams = getContextParams({});

  const config = useMemo(() => {
    const exportConfig = getEndpointByType(actionDefinition, 'START_EXPORT');

    return {
      exportConfig,
      exportContextParams: {
        ...contextParams,
        filter: {}
      }
    };
  }, [actionDefinition, contextParams]);

  return useEventCallback(
    values => {
      const context = get(values, 'applyFilters')
        ? config.exportContextParams
        : { ...config.exportContextParams, filter: undefined };
      const mergedConfig = {
        ...config.exportConfig,
        data: {
          parentId: config.parentId,
          ...values
        }
      };

      return Request.request(mergedConfig, context)
        .then(response => response.data)
        .then(createdExport => {
          toast.addToast(
            getAttribute(
              actionDefinition,
              'startedMessage',
              'Export started successfully!'
            ),
            {
              autoClose: 5000,
              closeOnClick: true,
              type: 'info'
            }
          );

          polling.startPoll(createdExport.id);
        })
        .catch(error => {
          if (!isString(error)) {
            error = getErrorMessage(error);
          }

          toast.addToast(error, {
            autoClose: 5000,
            closeOnClick: true,
            type: 'error'
          });

          logger.debug(
            'An error occurred while creating a new Export: ',
            error
          );
          return Promise.reject(error);
        });
    },
    [config]
  );
}

export default ExportTranslations;
export { ExportTranslations };
